import React, { useState, useMemo, useEffect, useRef } from 'react';
import { getServiceName } from '../utils/certificationService';

export const CertificationServiceDropdown = ({
  rowIndex,
  value,
  services,
  onChange,
  onKeyDown,
  onFocus,
  fieldIndex,
  selectedCells
}) => {
  const [pasteRowIndex, setPasteRowIndex] = useState(null);
  const dropdownRef = useRef(null);
  // Memoize display value to prevent unnecessary updates
  const displayValue = useMemo(() => {
    // If value is a number (service ID), show service name
    if (value && !isNaN(value)) {
      return getServiceName(value, services) || '';
    }
    // Otherwise show the raw input value
    return value || '';
  }, [value, services]);

  const handleInputChange = (e) => {
    onChange({ target: { value: e.target.value } });
  };

  const handleBlur = (e) => {
    const inputText = e.target.value.toLowerCase();
    const matchedService = services.find(service => 
      service.name.toLowerCase() === inputText
    );
    
    if (matchedService) {
      onChange({ target: { value: matchedService.id } });
    }
  };

  // Handle clicks outside the dropdown
  useEffect(() => {
    if (!pasteRowIndex) return; // Only add listener when dropdown is open

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setPasteRowIndex(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [pasteRowIndex]);

  const calculateDropdownPosition = (el) => {
    if (!el || pasteRowIndex !== rowIndex) return;

    const rect = el.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 200; // Max height of dropdown
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    // Position dropdown above if there's not enough space below
    // and more space above than below
    const positionAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
    
    if (positionAbove) {
      el.style.setProperty('--dropdown-top', `${rect.top + window.scrollY - dropdownHeight}px`);
    } else {
      el.style.setProperty('--dropdown-top', `${rect.bottom + window.scrollY}px`);
    }
    el.style.setProperty('--dropdown-left', `${rect.left + window.scrollX}px`);
    el.setAttribute('data-position', positionAbove ? 'above' : 'below');
  };

  const setRef = (el) => {
    dropdownRef.current = el;
    calculateDropdownPosition(el);
  };

  return (
    <div 
      className="flex relative" 
      ref={setRef}
    >
      <input
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onKeyDown={(e) => {
            // Handle Escape key to close dropdown
            if (e.key === 'Escape' && pasteRowIndex === rowIndex) {
              e.preventDefault();
              setPasteRowIndex(null);
              return;
            }
            
            // If dropdown is open and arrow key is pressed, close dropdown and allow navigation
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) && pasteRowIndex === rowIndex) {
              setPasteRowIndex(null);
            }
            
            // Always pass navigation keys to parent handler
            if (['Tab', 'Enter', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
              onKeyDown(e, rowIndex, fieldIndex);
            }
          }}
          onBlur={handleBlur}
          onFocus={onFocus}
          className="block w-full px-2 py-1 text-sm border-0 outline-none focus:ring-0 focus:outline-none"
          name="certification_service_id"
          data-row={rowIndex}
          data-field="certification_service_id"
        />
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setPasteRowIndex(pasteRowIndex === rowIndex ? null : rowIndex);
          }}
          className="px-2 border-l border-gray-200 hover:bg-gray-50"
        >
          <svg className="h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
        {pasteRowIndex === rowIndex && (
          <div 
            className="fixed z-50 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto"
            style={{
              width: '200px',
              top: 'var(--dropdown-top)',
              left: 'var(--dropdown-left)',
              maxHeight: '200px',
              position: 'fixed'
            }}
          >
            <div className="py-1">
              <div
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                onClick={() => {
                  onChange({ target: { value: '' } });
                  setPasteRowIndex(null);
                }}
              >
                None
              </div>
              {services.map(service => (
                <div
                  key={service.id}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => {
                    onChange({ target: { value: service.id } });
                    setPasteRowIndex(null);
                  }}
                >
                  {service.name}
                </div>
              ))}
            </div>
          </div>
        )}
    </div>
  );
};
